// src/App.js - Updated with all fixes
import React, { useState, useEffect, useRef } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
} from "react-router-dom";
import "./App.css";

// Main App Components
import { scriptureAPI } from "./services/api"; 
import ScriptureAnswer from "./components/ScriptureAnswer.jsx";
import Login from "./components/Login.jsx";
import AboutSection from "./components/AboutSection.jsx";
import TermsConditionsSection from "./components/TermsConditionsSection.jsx";
import PrivacyPolicy from "./components/PrivacyPolicy.jsx"; 
import PublicForum from "./components/PublicForum.jsx";
import Feedback from "./components/Feedback.jsx";
import ContactUs from "./components/ContactUs.jsx";
import AllQuestionsModal from "./components/AllQuestionsModal.jsx";
import AuthCallback from "./components/AuthCallback.jsx";
import ResetPassword from "./components/ResetPassword.jsx";
import { QUESTION_LIBRARY_CATEGORIES } from "./data/questionLibrary";
import useSpeechInput from './hooks/useSpeechInput';


// Admin Panel Components
import AdminLayout from "./components/Admin/AdminLayout";
import Dashboard from "./components/Admin/Dashboard";
import Users from "./components/Admin/Users";
import QuestionsPending from "./components/Admin/QuestionsPending";
import QuestionsAnswered from "./components/Admin/QuestionsAnswered";
import QuestionsAll from "./components/Admin/QuestionsAll";
import SearchHistory from "./components/Admin/SearchHistory";
import ActivityLogs from "./components/Admin/ActivityLogs";
import AdminFeedback from "./components/Admin/Feedback";

// Helper function to get stored user
const getStoredUser = () => {
  try {
    const savedUser = localStorage.getItem('user');
    return savedUser ? JSON.parse(savedUser) : null;
  } catch (error) {
    console.error("Error loading user from localStorage:", error);
    return null;
  }
};

// Admin Protection Wrapper
function RequireAdmin({ children }) {
  const user = getStoredUser();
  const isAdmin = user?.role === "ADMIN";

  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }
  
  return children;
}

// Main DharmaAnswers App Component
function MainApp() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [activeNav, setActiveNav] = useState('home');
  const [hoverStates, setHoverStates] = useState({});
  const [aiAnswer, setAiAnswer] = useState("");
  const [isMobile, setIsMobile] = useState(false);
  const [isSmallPhone, setIsSmallPhone] = useState(false);
  const [showSidebar, setShowSidebar] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [quickLinksExpanded, setQuickLinksExpanded] = useState(false);
  
  // Load user from localStorage
  const [user, setUser] = useState(() => getStoredUser());
  
  const [currentPage, setCurrentPage] = useState('home');
  const [showAllQuestions, setShowAllQuestions] = useState(false);
  const [selectedQuestionCategory, setSelectedQuestionCategory] = useState('all');
  const [recentQuestions, setRecentQuestions] = useState([
    {
      title: "How to deal with anger according to Bhagavad Gita?",
      category: "Daily Life & Dharma",
      views: "1.2k"
    },
    {
      title: "What is the importance of daily puja?",
      category: "Daily Life & Dharma",
      views: "893"
    },
    {
      title: "How to balance work and spiritual practice?",
      category: "Career & Duty",
      views: "756"
    },
    {
      title: "Is anger always wrong according to Hindu scriptures?",
      category: "Philosophy & Spiritual Growth",
      views: "642"
    },
    {
      title: "How to cultivate patience in difficult relationships?",
      category: "Relationships & Family",
      views: "589"
    },
    {
      title: "What is the concept of Dharma in workplace?",
      category: "Career & Duty",
      views: "521"
    },
    {
      title: "How to perform daily worship without a temple?",
      category: "Daily Life & Dharma",
      views: "487"
    },
    {
      title: "The meaning of Om in meditation",
      category: "Philosophy & Spiritual Growth",
      views: "432"
    }
  ]);

  // Refs for scrolling
  const recentQuestionsRef = useRef(null);
  const sidebarRef = useRef(null);
  const resultsSectionRef = useRef(null);
  const pendingResultsScrollRef = useRef(false);

  // Detect screen sizes
  useEffect(() => {
    const checkScreenSize = () => {
      const width = window.innerWidth;
      setIsMobile(width <= 768);
      setIsSmallPhone(width <= 480);
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  useEffect(() => {
    if (!showResults || currentPage !== 'home' || !pendingResultsScrollRef.current) {
      return;
    }

    const scrollToResults = () => {
      if (resultsSectionRef.current) {
        resultsSectionRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }
      pendingResultsScrollRef.current = false;
    };

    const timeoutId = window.setTimeout(scrollToResults, 120);

    return () => window.clearTimeout(timeoutId);
  }, [showResults, currentPage, isLoading, searchResults.length]);

  // Save user to localStorage (FIXED: removed storage event dispatch)
  useEffect(() => {
    try {
      if (user) {
        localStorage.setItem('user', JSON.stringify(user));
      } else {
        localStorage.removeItem('user');
        localStorage.removeItem('token');
      }
    } catch (error) {
      console.error("Error saving user to localStorage:", error);
    }
  }, [user]);

  // Function to load recent user questions
  const loadRecentUserQuestions = async (userId) => {
    try {
      const { data } = await scriptureAPI.getRecentQuestions(userId);

      const formattedQuestions = data.map((item) => ({
        id: item.id,
        title: item.question || item.text || "Untitled Question",
        category: item.category || "Scripture Guidance",
        views: item.views || "0",
      }));

      setRecentQuestions(formattedQuestions);
    } catch (err) {
      console.error("Failed to load recent questions:", err);
    }
  };

  // Login functions
  const handleLoginClick = () => {
    setShowLogin(true);
    setShowSidebar(false);
  };

  const handleLogin = async (userData) => {
    if (!userData || !userData.email) {
      console.error("Invalid user data received");
      return;
    }
    
    const formattedUser = {
      id: userData.id || Date.now(),
      name: userData.name || "User",
      email: userData.email,
      username: userData.username || userData.email.split('@')[0],
      token: userData.token || null,
      role: userData.role || 'USER'
    };
    
    setUser(formattedUser);
    setShowLogin(false);
    
    if (formattedUser.id) {
      await loadRecentUserQuestions(formattedUser.id);
    }
  };

  const handleLogout = () => {
    setUser(null);
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    window.location.href = '/';
  };

  const handleGoHome = () => {
    setCurrentPage('home');
    setShowResults(false);
    setSearchTerm('');
    setSearchResults([]);
    setShowSidebar(false);
    setQuickLinksExpanded(false);
    navigate('/');
  };

  const handleCloseQuickLinkPage = () => {
    handleGoHome();
    window.setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 60);
  };

  const renderClosablePage = (content, useContentContainer = false) => (
    <div style={styles.quickLinkPageShell}>
      <button
        type="button"
        style={styles.quickLinkCloseButton}
        onClick={handleCloseQuickLinkPage}
        aria-label="Close section"
        title="Close"
      >
        ×
      </button>
      {useContentContainer ? (
        <div style={styles.contentContainer}>{content}</div>
      ) : (
        content
      )}
    </div>
  );

  // Enhanced sample data
  const featuredQuestions = [
    {
      id: 1,
      title: "Is anger always wrong according to Hindu scriptures?",
      summary: "Understanding the different types of anger and when it's justified versus destructive, with references from Bhagavad Gita and Upanishads.",
      category: "Daily Life & Dharma",
      date: "2 days ago",
      icon: "🔥",
      tags: ["Bhagavad Gita", "Emotions", "Self-control"]
    },
    {
      id: 2,
      title: "How to perform daily worship without a temple?",
      summary: "Comprehensive guidance for establishing personal worship at home with simple rituals and minimal requirements for modern life.",
      category: "Daily Life & Dharma",
      date: "1 week ago",
      icon: "🛕",
      tags: ["Puja", "Rituals", "Home Worship"]
    },
    {
      id: 3,
      title: "What is the concept of Dharma in workplace?",
      summary: "Applying ancient principles of righteousness and duty in contemporary professional life according to Bhagavad Gita teachings.",
      category: "Career & Duty",
      date: "3 days ago",
      icon: "💼",
      tags: ["Karma Yoga", "Professional Life", "Ethics"]
    },
    {
      id: 4,
      title: "How to cultivate patience in difficult relationships?",
      summary: "Scriptural wisdom and practical techniques for maintaining harmony and patience in challenging family and social situations.",
      category: "Relationships & Family",
      date: "5 days ago",
      icon: "❤️",
      tags: ["Relationships", "Patience", "Family Dharma"]
    }
  ];

  const categories = [
    { name: "Daily Life & Dharma", icon: "🌅", count: 24 },
    { name: "Relationships & Family", icon: "👨‍👩‍👧‍👦", count: 18 },
    { name: "Career & Duty", icon: "💼", count: 15 },
    { name: "Festivals & Rituals", icon: "🎊", count: 12 },
    { name: "Philosophy & Spiritual Growth", icon: "🧘", count: 22 }
  ];

  const sidebarCategories = QUESTION_LIBRARY_CATEGORIES.filter(
    (category) => category.id !== 'all'
  ).map((category) => ({
    name: category.name,
    icon: category.icon,
    count: category.count
  }));

  // Updated additional links with Privacy Policy
  const additionalLinks = [
    { name: "Ask Shia", icon: "💬", page: "forum" },
    { name: "About", icon: "ℹ️", page: "about" },
    { name: "Terms & Conditions", icon: "📄", page: "terms" },
    { name: "Privacy Policy", icon: "🔒", page: "privacy" },
    { name: "Feedback", icon: "📝", page: "feedback" },
    { name: "Contact Us", icon: "📞", page: "contact" }
  ];

  const scriptures = [
    { name: "Bhagavad Gita", icon: "📖" },
    { name: "Upanishads", icon: "📜" },
    { name: "Ramayana", icon: "🏹" },
    { name: "Mahabharata", icon: "⚔️" }
  ];

  // Handle page navigation
  const handlePageChange = (page) => {
    setCurrentPage(page);
    setShowResults(false);
    setShowSidebar(false);
    setQuickLinksExpanded(false);
  };

  // Handle logo click - return to home
  const handleLogoClick = () => {
    handleGoHome();
  };

  const openQuestionLibrary = (category = 'all') => {
    setSelectedQuestionCategory(category);
    setShowAllQuestions(true);
    setShowSidebar(false);
    setQuickLinksExpanded(false);
  };

  // Search handler
  const handleSearch = async (searchQuery) => {
    const query = (searchQuery ?? searchTerm).trim();
    if (!query) return;

    setSearchTerm(query);
    pendingResultsScrollRef.current = true;
    setIsLoading(true);
    setShowResults(true);
    setCurrentPage('home');
    setShowSidebar(false);
    setQuickLinksExpanded(false);
    if (location.pathname !== '/') {
      navigate('/');
    }

    try {
      const data = await scriptureAPI.askQuestion(query, user?.id || null);

      if (data?.error) {
        throw new Error(data.answer || "API error");
      }
      
      setSearchResults([
        {
          question: query,
          ...data,
        },
      ]);

      if (user?.id) {
        await loadRecentUserQuestions(user.id);
      }

    } catch (error) {
      console.error('Search error:', error);
      const localResults = getLocalScriptureAnswers(query);
      setSearchResults(localResults);
    } finally {
      setIsLoading(false);
    }
  };

  const getLocalScriptureAnswers = (query) => {
    const queryLower = query.toLowerCase();
    const answers = [];

    if (queryLower.includes('anger') || queryLower.includes('angry')) {
      answers.push({
        question: "How to deal with anger?",
        answer: "🌿 **Spiritual Growth and Self-Acceptance for Marriage Prospects** 🌿**Introduction**✧ Facing rejection due to physical appearance can be disheartening. Hindu scriptures emphasize inner strength, self-acceptance, and focusing on qualities beyond the physical. The following guidance helps you cultivate inner peace and attract a compatible partner.**📜 Divine Wisdom from Scripture** **🔍 Key Spiritual Insights**• The path of dharma leads to inner harmony and ultimate liberation. **🛠️ Practical Application** **1. Cultivate Self-Love and Acceptance** ▸ Embrace your body as it is. Focus on your strengths, talents, and positive qualities. Work on improving yourself holistically – mentally, emotionally, and spiritually. When you radiate self-love and confidence, others will be drawn to your positive energy. **2. Focus on Inner Qualities**",
        reference: "Bhagavad Gita 2.62-63",
        scripture: "Bhagavad Gita",
      });
    }

    if (queryLower.includes('peace') || queryLower.includes('calm')) {
      answers.push({
        question: "How to find inner peace?",
        answer: "The Bhagavad Gita teaches that peace comes from self-control, performing one's duty without attachment to results, and taking refuge in the Supreme.",
        reference: "Bhagavad Gita 2.66",
        scripture: "Bhagavad Gita",
      });
    }

    return answers.length > 0 ? answers : [{
      question: query,
      answer: "For guidance on this topic, the Bhagavad Gita Chapter 2 provides fundamental spiritual wisdom about duty, self-realization, and finding inner peace through performing one's responsibilities without attachment to results.",
      reference: "Bhagavad Gita Chapter 2",
      scripture: "Bhagavad Gita",
    }];
  };

  const handleMouseEnter = (id) => {
    if (!isMobile) {
      setHoverStates((prev) => ({ ...prev, [id]: true }));
    }
  };

  const handleMouseLeave = (id) => {
    if (!isMobile) {
      setHoverStates((prev) => ({ ...prev, [id]: false }));
    }
  };

  const handleHeroSearch = () => {
    handleSearch(searchTerm);
  };



  const { isListening, isSupported, toggleListening } =
    useSpeechInput((text) => {
    setSearchTerm(text);
  });





  const toggleSidebar = () => {
    setShowSidebar(!showSidebar);
    setQuickLinksExpanded(false);
  };

  const toggleQuickLinks = () => {
    setQuickLinksExpanded(!quickLinksExpanded);
  };

  // Handle question click from modal
  const handleQuestionClickFromModal = (question) => {
    setSearchTerm(question);
    handleSearch(question);
    setShowAllQuestions(false);
  };

  // Enhanced responsive styles with small phone optimizations
  const styles = {
    app: {
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0a0a0a 0%, #1a1a2e 50%, #16213e 100%)',
      fontFamily: '"Inter", "SF Pro Display", -apple-system, BlinkMacSystemFont, sans-serif',
      color: '#ffffff',
      lineHeight: '1.6',
      position: 'relative',
      overflowX: 'hidden'
    },
    backgroundGraphic: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundImage: `url('https://example.com/your-background.jpg')`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
      zIndex: 0,
      pointerEvents: 'none'
    },

    // Enhanced Top Navigation (responsive)
    topBar: {
      background: 'rgba(10, 10, 10, 0.95)',
      backdropFilter: 'blur(20px)',
      borderBottom: '1px solid rgba(97, 218, 251, 0.15)',
      boxShadow: '0 8px 32px rgba(97, 218, 251, 0.08)',
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      zIndex: 1000,
      padding: isSmallPhone ? '6px 0' : (isMobile ? '8px 0' : '12px 0')
    },
    topBarContent: {
      maxWidth: '1400px',
      margin: '0 auto',
      padding: isSmallPhone ? '0 12px' : (isMobile ? '0 16px' : '0 32px'),
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between'
    },
    logo: {
      display: 'flex',
      alignItems: 'center',
      gap: isSmallPhone ? '6px' : '8px',
      textDecoration: 'none',
      cursor: 'pointer',
      flexShrink: 0
    },
    logoIcon: {
      width: isSmallPhone ? '28px' : (isMobile ? '32px' : '40px'),
      height: isSmallPhone ? '28px' : (isMobile ? '32px' : '40px'),
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundImage: `url(${process.env.PUBLIC_URL}/dharma-logo.png)`,
      backgroundSize: 'contain',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
      mixBlendMode: 'screen',
      filter: 'drop-shadow(0 0 8px rgba(97, 218, 251, 0.35)) drop-shadow(0 0 20px rgba(97, 218, 251, 0.22))',
      fontSize: 0,
      color: 'transparent',
      flexShrink: 0
    },
    logoImage: {
      width: '100%',
      height: '100%',
      objectFit: 'contain',
      borderRadius: '10px',
      boxShadow: '0 4px 15px rgba(97, 218, 251, 0.3)',
      display: 'block'
    },
    logoText: {
      fontSize: isSmallPhone ? '1rem' : (isMobile ? '1.2rem' : '1.6rem'),
      fontWeight: '800',
      background: 'linear-gradient(135deg, #61dafb 0%, #21a1f1 100%)',
      backgroundClip: 'text',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      textShadow: '0 0 30px rgba(97, 218, 251, 0.5)'
    },
    homeButton: {
      display: 'flex',
      background: 'rgba(97, 218, 251, 0.1)',
      border: '1px solid rgba(97, 218, 251, 0.2)',
      borderRadius: '12px',
      padding: isSmallPhone ? '8px' : (isMobile ? '10px' : '12px'),
      color: '#61dafb',
      cursor: 'pointer',
      fontSize: isSmallPhone ? '16px' : (isMobile ? '18px' : '20px'),
      fontWeight: 'bold',
      alignItems: 'center',
      justifyContent: 'center',
      border: 'none',
      marginLeft: isSmallPhone ? '6px' : (isMobile ? '8px' : '12px')
    },
    mobileMenuButton: {
      display: isMobile ? 'flex' : 'none',
      background: 'rgba(0, 0, 0, 0.8)',
      border: '1px solid rgba(97, 218, 251, 0.3)',
      borderRadius: '10px',
      padding: isSmallPhone ? '8px' : '10px',
      color: '#61dafb',
      cursor: 'pointer',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: isSmallPhone ? '16px' : '18px',
      fontWeight: 'bold',
      marginLeft: 'auto',
      border: 'none'
    },
    loginButtonTopBar: {
      display: 'flex',
      background: 'rgba(97, 218, 251, 0.1)',
      border: '1px solid rgba(97, 218, 251, 0.2)',
      borderRadius: '12px',
      padding: isSmallPhone ? '8px 12px' : '10px 20px',
      color: '#61dafb',
      cursor: 'pointer',
      fontSize: isSmallPhone ? '0.8rem' : (isMobile ? '0.85rem' : '0.9rem'),
      fontWeight: '600',
      transition: 'all 0.3s ease',
      textDecoration: 'none',
      alignItems: 'center',
      justifyContent: 'center',
      border: 'none'
    },

    // Mobile Sidebar Overlay
    mobileSidebarOverlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      zIndex: 999,
      display: isMobile && showSidebar ? 'block' : 'none'
    },

    mobileSidebar: {
      position: 'fixed',
      top: isSmallPhone ? '50px' : '60px',
      right: isSmallPhone ? '8px' : '16px',
      width: isSmallPhone ? '260px' : '280px',
      background: 'rgba(15, 15, 15, 0.95)',
      backdropFilter: 'blur(20px)',
      borderRadius: '16px',
      boxShadow: '0 20px 60px rgba(97, 218, 251, 0.3)',
      zIndex: 1000,
      padding: isSmallPhone ? '16px' : '20px',
      overflowY: 'auto',
      maxHeight: 'calc(100vh - 70px)',
      border: '1px solid rgba(97, 218, 251, 0.2)',
      display: isMobile && showSidebar ? 'block' : 'none'
    },

    // Main Content
    mainContent: {
      maxWidth: '1400px',
      margin: '0 auto',
      padding: isSmallPhone ? '60px 12px 20px 12px' : (isMobile ? '70px 16px 30px 16px' : '100px 32px 60px 32px'),
      display: 'flex',
      gap: isMobile ? '0' : '40px',
      position: 'relative',
      zIndex: 1,
      flexDirection: isMobile ? 'column' : 'row'
    },

    // Sidebar - FIXED POSITION IN DESKTOP
    sidebar: {
      width: isMobile ? '100%' : '332px',
      background: isMobile
        ? 'rgba(15, 15, 15, 0.85)'
        : 'linear-gradient(180deg, rgba(9, 12, 18, 0.96) 0%, rgba(11, 14, 22, 0.98) 46%, rgba(8, 10, 16, 0.98) 100%)',
      backdropFilter: 'blur(20px)',
      borderRadius: isMobile ? '0' : '24px',
      boxShadow: isMobile
        ? '0 20px 60px rgba(97, 218, 251, 0.1)'
        : '0 26px 70px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(97, 218, 251, 0.08)',
      padding: isSmallPhone ? '16px' : (isMobile ? '20px' : '24px'),
      height: isMobile ? 'auto' : 'calc(100vh - 140px)',
      position: isMobile ? 'static' : 'fixed', // CHANGED TO FIXED
      top: isMobile ? '0' : '100px',
      left: isMobile ? '0' : '32px', // Added left positioning
      bottom: isMobile ? 'auto' : '20px',
      border: isMobile ? 'none' : '1px solid rgba(97, 218, 251, 0.14)',
      display: isMobile ? 'none' : 'flex', // Changed to flex
      flexDirection: 'column',
      overflow: isMobile ? 'visible' : 'hidden',
      flexShrink: 0,
      zIndex: 10
    },

    // User Info Box
    userInfoBox: {
      background: 'linear-gradient(145deg, rgba(97, 218, 251, 0.11) 0%, rgba(33, 161, 241, 0.08) 48%, rgba(16, 28, 40, 0.9) 100%)',
      borderRadius: '20px',
      padding: '22px',
      textAlign: 'center',
      marginBottom: '24px',
      border: 'none',
      boxShadow: '0 18px 36px rgba(0, 0, 0, 0.24), inset 0 0 0 1px rgba(27, 42, 60, 0.9)'
    },
    userInfoIcon: {
      fontSize: '2.5rem',
      marginBottom: '12px'
    },
    userInfoName: {
      fontSize: '1.2rem',
      fontWeight: '700',
      color: '#61dafb',
      marginBottom: '8px'
    },
    userInfoEmail: {
      fontSize: '0.9rem',
      color: '#94bbe9',
      opacity: 0.8,
      marginBottom: '16px',
      wordBreak: 'break-all'
    },
    userInfoButton: {
      background: 'rgba(239, 68, 68, 0.1)',
      color: '#ef4444',
      border: '1px solid rgba(239, 68, 68, 0.3)',
      borderRadius: '12px',
      padding: '10px 20px',
      fontSize: '0.9rem',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      width: '100%',
      marginBottom: '8px',
      border: 'none',
      outline: 'none',
      appearance: 'none',
      WebkitAppearance: 'none',
      WebkitTapHighlightColor: 'transparent'
    },
    adminPanelButton: {
      backgroundColor: '#4f46e5',
      color: 'white',
      border: 'none',
      borderRadius: '12px',
      padding: '10px 20px',
      fontSize: '0.9rem',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      width: '100%',
      marginBottom: '8px',
      outline: 'none',
      appearance: 'none',
      WebkitAppearance: 'none',
      WebkitTapHighlightColor: 'transparent'
    },

    // Scrollable Content Area in Sidebar
    sidebarScrollableContent: {
      flex: 1,
      overflowY: 'auto',
      marginBottom: '18px',
      paddingRight: '8px'
    },

    // Fixed Quick Links at Bottom
    quickLinksFixed: {
      flexShrink: 0,
      marginTop: 'auto',
      paddingTop: '18px',
      borderTop: '1px solid rgba(97, 218, 251, 0.12)',
      background: isMobile ? 'transparent' : 'linear-gradient(180deg, rgba(8, 12, 18, 0) 0%, rgba(8, 12, 18, 0.92) 32%)'
    },

    navSection: {
      marginBottom: isSmallPhone ? '20px' : (isMobile ? '24px' : '28px')
    },
    sectionTitle: {
      fontWeight: '700',
      color: '#79e4ff',
      marginBottom: isSmallPhone ? '12px' : (isMobile ? '16px' : '14px'),
      fontSize: isSmallPhone ? '0.75rem' : (isMobile ? '0.8rem' : '0.85rem'),
      textTransform: 'uppercase',
      letterSpacing: '1.6px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      paddingBottom: isMobile ? '0' : '10px',
      borderBottom: isMobile ? 'none' : '1px solid rgba(97, 218, 251, 0.08)'
    },
    categoryItem: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: isSmallPhone ? '10px 12px' : (isMobile ? '12px 16px' : '16px 20px'),
      borderRadius: '18px',
      color: '#e2e8f0',
      textDecoration: 'none',
      fontSize: isSmallPhone ? '0.85rem' : (isMobile ? '0.9rem' : '0.95rem'),
      marginBottom: isMobile ? '6px' : '10px',
      transition: 'all 0.3s ease',
      fontWeight: '600',
      background: 'linear-gradient(135deg, rgba(25, 29, 38, 0.96) 0%, rgba(19, 22, 30, 0.94) 100%)',
      border: 'none',
      width: '100%',
      textAlign: 'left',
      cursor: 'pointer',
      boxShadow: '0 10px 24px rgba(0,0,0,0.18), inset 0 0 0 1px rgba(27, 42, 60, 0.9)',
      backgroundClip: 'padding-box',
      outline: 'none',
      appearance: 'none',
      WebkitAppearance: 'none',
      WebkitTapHighlightColor: 'transparent'
    },
    categoryCount: {
      background: 'linear-gradient(135deg, rgba(97, 218, 251, 0.16) 0%, rgba(22, 31, 42, 0.9) 100%)',
      color: '#8fe6ff',
      padding: '5px 10px',
      borderRadius: '20px',
      fontSize: '0.72rem',
      fontWeight: '700',
      border: '1px solid rgba(97, 218, 251, 0.12)',
      boxShadow: 'none'
    },
    recentQuestion: {
      display: 'block',
      padding: isSmallPhone ? '12px' : (isMobile ? '16px' : '16px 18px'),
      borderRadius: '18px',
      textDecoration: 'none',
      marginBottom: isMobile ? '10px' : '12px',
      background: 'linear-gradient(145deg, rgba(26, 28, 33, 0.98) 0%, rgba(18, 21, 27, 0.95) 100%)',
      border: 'none',
      transition: 'all 0.3s ease',
      width: '100%',
      textAlign: 'left',
      cursor: 'pointer',
      boxShadow: '0 12px 26px rgba(0, 0, 0, 0.18), inset 0 0 0 1px rgba(27, 42, 60, 0.9)',
      backgroundClip: 'padding-box',
      outline: 'none',
      appearance: 'none',
      WebkitAppearance: 'none',
      WebkitTapHighlightColor: 'transparent'
    },
    additionalLink: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: isSmallPhone ? '12px 16px' : (isMobile ? '14px 18px' : '16px 20px'),
      borderRadius: '18px',
      color: '#e2e8f0',
      textDecoration: 'none',
      fontSize: isSmallPhone ? '0.85rem' : (isMobile ? '0.9rem' : '0.95rem'),
      marginBottom: isMobile ? '8px' : '10px',
      transition: 'all 0.3s ease',
      fontWeight: '600',
      background: 'linear-gradient(135deg, rgba(24, 28, 35, 0.98) 0%, rgba(18, 21, 27, 0.95) 100%)',
      border: 'none',
      width: '100%',
      textAlign: 'left',
      cursor: 'pointer',
      boxShadow: '0 10px 24px rgba(0,0,0,0.16), inset 0 0 0 1px rgba(27, 42, 60, 0.9)',
      backgroundClip: 'padding-box',
      outline: 'none',
      appearance: 'none',
      WebkitAppearance: 'none',
      WebkitTapHighlightColor: 'transparent'
    },
    additionalLinkActive: {
      background: 'linear-gradient(135deg, rgba(97, 218, 251, 0.12) 0%, rgba(21, 33, 45, 0.96) 100%)',
      border: 'none',
      boxShadow: '0 10px 24px rgba(0,0,0,0.16), inset 0 0 0 1px rgba(62, 125, 162, 0.42)'
    },
    sidebarItemLabel: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: isMobile ? '10px' : '12px',
      minWidth: 0
    },
    sidebarItemIcon: {
      width: isMobile ? '28px' : '30px',
      height: isMobile ? '28px' : '30px',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: '10px',
      background: 'linear-gradient(145deg, rgba(97, 218, 251, 0.16) 0%, rgba(18, 24, 32, 0.88) 100%)',
      color: '#9de8ff',
      boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.03)',
      flexShrink: 0
    },
    sidebarItemArrow: {
      color: 'rgba(143, 230, 255, 0.65)',
      fontSize: '0.82rem',
      fontWeight: '700',
      flexShrink: 0
    },
    quickLinksToggleIcon: {
      marginLeft: 'auto',
      fontSize: '0.78rem',
      color: '#8fe6ff'
    },
    questionTitleSmall: {
      fontSize: isSmallPhone ? '0.85rem' : (isMobile ? '0.9rem' : '0.95rem'),
      fontWeight: '650',
      color: '#ffffff',
      marginBottom: '8px',
      lineHeight: '1.45',
      display: '-webkit-box',
      WebkitLineClamp: 2,
      WebkitBoxOrient: 'vertical',
      overflow: 'hidden'
    },
    questionMeta: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: '10px'
    },
    questionCategory: {
      fontSize: isSmallPhone ? '0.7rem' : (isMobile ? '0.75rem' : '0.78rem'),
      color: '#7ddfff',
      fontWeight: '700',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '6px',
      minWidth: 0
    },
    questionViews: {
      fontSize: isSmallPhone ? '0.65rem' : (isMobile ? '0.7rem' : '0.72rem'),
      color: '#8ea5c2',
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
      padding: '4px 8px',
      borderRadius: '999px',
      background: 'rgba(97, 218, 251, 0.06)',
      border: '1px solid rgba(97, 218, 251, 0.08)',
      flexShrink: 0
    },

    // Main Area - Adjusted for fixed sidebar
    mainArea: {
      flex: 1,
      minWidth: 0,
      width: '100%',
      marginLeft: isMobile ? '0' : 'calc(332px + 40px)' // Add space for fixed sidebar
    },

    // Hero Section
    hero: {
      background: 'linear-gradient(135deg, rgba(15, 15, 15, 0.85) 0%, rgba(20, 20, 20, 0.90) 100%)',
      backdropFilter: 'blur(20px)',
      borderRadius: isSmallPhone ? '16px' : (isMobile ? '20px' : '28px'),
      boxShadow: '0 25px 70px rgba(97, 218, 251, 0.15)',
      padding: isSmallPhone ? '24px 16px' : (isMobile ? '30px 20px' : '80px 60px'),
      textAlign: 'center',
      marginBottom: isSmallPhone ? '16px' : (isMobile ? '20px' : '40px'),
      border: '1px solid rgba(97, 218, 251, 0.2)',
      position: 'relative',
      overflow: 'hidden'
    },
    heroBackground: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: `
        radial-gradient(circle at 20% 20%, rgba(97, 218, 251, 0.08) 0%, transparent 50%),
        radial-gradient(circle at 80% 20%, rgba(33, 161, 241, 0.06) 0%, transparent 50%),
        radial-gradient(circle at 40% 80%, rgba(97, 218, 251, 0.05) 0%, transparent 50%)
      `,
      zIndex: 0
    },
    heroContent: {
      position: 'relative',
      zIndex: 1,
      maxWidth: '800px',
      margin: '0 auto'
    },
    heroTitle: {
      fontSize: isSmallPhone ? '1.8rem' : (isMobile ? '2.2rem' : '3.5rem'),
      fontWeight: '800',
      background: 'linear-gradient(135deg, #61dafb 0%, #21a1f1 50%, #61dafb 100%)',
      backgroundClip: 'text',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      marginBottom: isSmallPhone ? '12px' : (isMobile ? '16px' : '24px'),
      lineHeight: '1.1',
      textShadow: '0 0 40px rgba(97, 218, 251, 0.4)'
    },
    heroSubtitle: {
      fontSize: isSmallPhone ? '0.95rem' : (isMobile ? '1.05rem' : '1.3rem'),
      color: '#a0aec0',
      marginBottom: isSmallPhone ? '20px' : (isMobile ? '25px' : '50px'),
      lineHeight: '1.6',
      fontWeight: '400'
    },
    heroSearch: {
      maxWidth: '600px',
      margin: '0 auto 24px auto',
      position: 'relative',
      width: '100%'
    },


    // heroInput: {
    //   width: '100%',
    //   background: 'rgba(20, 20, 20, 0.9)',
    //   border: '2px solid rgba(97, 218, 251, 0.25)',
    //   borderRadius: '20px',
    //   padding: isSmallPhone ? '12px 16px' : (isMobile ? '14px 18px' : '20px 30px'),
    //   fontSize: isSmallPhone ? '0.85rem' : (isMobile ? '0.95rem' : '1.1rem'),
    //   outline: 'none',
    //   transition: 'all 0.3s ease',
    //   boxShadow: '0 8px 40px rgba(97, 218, 251, 0.15)',
    //   color: '#ffffff',
    //   border: 'none'
    // },



    heroInput: {
  width: '100%',
  background: 'rgba(20, 20, 20, 0.9)',
  border: '2px solid rgba(97, 218, 251, 0.25)',
  borderRadius: '20px',
  padding: isSmallPhone ? '14px 52px 14px 16px' : (isMobile ? '16px 52px 16px 18px' : '20px 56px 20px 30px'),
  fontSize: isSmallPhone ? '16px' : (isMobile ? '16px' : '1.1rem'),
  outline: 'none',
  transition: 'all 0.3s ease',
  boxShadow: '0 8px 40px rgba(97, 218, 251, 0.15)',
  color: '#ffffff',
  border: 'none',
  boxSizing: 'border-box',
  minHeight: isSmallPhone ? '52px' : (isMobile ? '52px' : 'auto'),
},

    heroSearchButton: {
      background: 'linear-gradient(135deg, #61dafb 0%, #21a1f1 100%)',
      color: 'black',
      padding: isSmallPhone ? '12px 20px' : (isMobile ? '14px 24px' : '16px 32px'),
      borderRadius: '18px',
      border: 'none',
      fontSize: isSmallPhone ? '0.9rem' : (isMobile ? '1rem' : '1.05rem'),
      fontWeight: '700',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      boxShadow: '0 6px 25px rgba(97, 218, 251, 0.3)',
      marginTop: '12px',
      minWidth: isSmallPhone ? '160px' : (isMobile ? '180px' : '200px'),
    },




    scriptureGrid: {
      display: 'flex',
      justifyContent: 'center',
      gap: isSmallPhone ? '8px' : (isMobile ? '12px' : '20px'),
      marginTop: isSmallPhone ? '16px' : (isMobile ? '20px' : '40px'),
      flexWrap: 'wrap'
    },
    scriptureItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      padding: isSmallPhone ? '6px 10px' : (isMobile ? '8px 12px' : '12px 20px'),
      background: 'rgba(97, 218, 251, 0.1)',
      borderRadius: '12px',
      border: '1px solid rgba(97, 218, 251, 0.2)',
      color: '#61dafb',
      fontSize: isSmallPhone ? '0.75rem' : (isMobile ? '0.8rem' : '0.9rem'),
      fontWeight: '600'
    },

    // Content Container for About and Terms
    contentContainer: {
      background: 'rgba(15, 15, 15, 0.85)',
      backdropFilter: 'blur(20px)',
      borderRadius: isSmallPhone ? '16px' : (isMobile ? '20px' : '28px'),
      padding: isSmallPhone ? '20px' : (isMobile ? '24px' : '40px'),
      marginBottom: isSmallPhone ? '16px' : (isMobile ? '20px' : '40px'),
      border: '1px solid rgba(97, 218, 251, 0.2)',
      boxShadow: '0 20px 60px rgba(97, 218, 251, 0.1)'
    },
    quickLinkPageShell: {
      position: 'relative'
    },
    quickLinkCloseButton: {
      position: 'absolute',
      top: isSmallPhone ? '10px' : (isMobile ? '12px' : '16px'),
      right: isSmallPhone ? '10px' : (isMobile ? '12px' : '16px'),
      width: isSmallPhone ? '34px' : (isMobile ? '38px' : '42px'),
      height: isSmallPhone ? '34px' : (isMobile ? '38px' : '42px'),
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'rgba(8, 12, 18, 0.92)',
      backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%237fdcff' stroke-width='2.3' stroke-linecap='round'%3E%3Cpath d='M6 6L18 18M18 6L6 18'/%3E%3C/svg%3E\")",
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'center',
      backgroundSize: isSmallPhone ? '14px 14px' : (isMobile ? '16px 16px' : '18px 18px'),
      color: 'transparent',
      border: '1px solid rgba(97, 218, 251, 0.22)',
      borderRadius: '12px',
      cursor: 'pointer',
      fontSize: 0,
      fontWeight: '700',
      lineHeight: 1,
      textIndent: '-9999px',
      overflow: 'hidden',
      whiteSpace: 'nowrap',
      zIndex: 20,
      boxShadow: '0 10px 28px rgba(0, 0, 0, 0.28), 0 0 18px rgba(97, 218, 251, 0.08)',
      backdropFilter: 'blur(12px)',
      transition: 'all 0.3s ease'
    },

    // Search Results Slide
    resultsSlide: {
      background: 'rgba(15, 15, 15, 0.85)',
      backdropFilter: 'blur(20px)',
      borderRadius: isSmallPhone ? '14px' : (isMobile ? '18px' : '24px'),
      padding: isSmallPhone ? '20px' : (isMobile ? '24px' : '40px'),
      marginBottom: isSmallPhone ? '16px' : (isMobile ? '20px' : '40px'),
      border: '1px solid rgba(97, 218, 251, 0.2)',
      boxShadow: '0 20px 60px rgba(97, 218, 251, 0.1)',
      scrollMarginTop: isMobile ? '92px' : '108px'
    },
    resultsHeader: {
      display: 'flex',
      alignItems: 'center',
      gap: isSmallPhone ? '10px' : (isMobile ? '12px' : '16px'),
      marginBottom: isSmallPhone ? '20px' : (isMobile ? '24px' : '32px'),
      paddingBottom: isSmallPhone ? '14px' : (isMobile ? '16px' : '20px'),
      borderBottom: '1px solid rgba(97, 218, 251, 0.1)',
      flexDirection: isMobile ? 'column' : 'row',
      textAlign: isMobile ? 'center' : 'left'
    },
    resultsIcon: {
      backgroundImage: `url(${process.env.PUBLIC_URL}/dharma-logo.png)`,
      backgroundSize: 'contain',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
      borderRadius: '12px',
      width: isSmallPhone ? '45px' : (isMobile ? '50px' : '60px'),
      height: isSmallPhone ? '45px' : (isMobile ? '50px' : '60px'),
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      mixBlendMode: 'screen',
      filter: 'drop-shadow(0 0 18px rgba(97, 218, 251, 0.35))'
    },
    resultsHeaderText: {
      flex: 1
    },
    resultsTitle: {
      margin: 0,
      color: '#61dafb',
      fontSize: isSmallPhone ? '1.2rem' : (isMobile ? '1.4rem' : '1.9rem'),
      fontWeight: '700',
      lineHeight: isMobile ? '1.35' : '1.3',
      flex: 1
    },
    searchQuery: {
      color: '#ffffff',
      fontStyle: 'italic',
      wordBreak: 'break-word'
    },
    resultsEyebrow: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: isMobile ? 'center' : 'flex-start',
      color: '#7fd3ff',
      fontSize: isSmallPhone ? '0.74rem' : '0.82rem',
      fontWeight: '700',
      letterSpacing: '0.12em',
      textTransform: 'uppercase',
      marginBottom: '8px'
    },
    resultsQuestion: {
      margin: 0,
      color: '#ffffff',
      fontSize: isSmallPhone ? '1.22rem' : (isMobile ? '1.48rem' : '2rem'),
      fontWeight: '700',
      fontStyle: 'italic',
      lineHeight: isMobile ? '1.35' : '1.28',
      wordBreak: 'break-word'
    },
    resultCard: {
      background: 'linear-gradient(135deg, rgba(12, 12, 14, 0.96) 0%, rgba(20, 22, 32, 0.96) 46%, rgba(24, 35, 56, 0.94) 100%)',
      borderRadius: isSmallPhone ? '14px' : (isMobile ? '16px' : '20px'),
      padding: isSmallPhone ? '16px' : (isMobile ? '20px' : '32px'),
      marginBottom: isSmallPhone ? '12px' : (isMobile ? '16px' : '24px'),
      border: '1px solid rgba(97, 218, 251, 0.15)',
      transition: 'all 0.3s ease',
      position: 'relative',
      overflow: 'hidden',
      boxShadow: '0 22px 46px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255,255,255,0.04)'
    },
    scriptureBadge: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '6px',
      background: 'linear-gradient(135deg, rgba(97, 218, 251, 0.15) 0%, rgba(33, 161, 241, 0.15) 100%)',
      color: '#61dafb',
      padding: isSmallPhone ? '6px 12px' : (isMobile ? '8px 14px' : '10px 18px'),
      borderRadius: '12px',
      fontSize: isSmallPhone ? '0.75rem' : (isMobile ? '0.8rem' : '0.9rem'),
      fontWeight: '700',
      marginBottom: isSmallPhone ? '12px' : (isMobile ? '16px' : '20px'),
      border: '1px solid rgba(97, 218, 251, 0.2)'
    },
    answerContent: {
      color: '#e2e8f0',
      fontSize: isSmallPhone ? '0.9rem' : (isMobile ? '1rem' : '1.1rem'),
      lineHeight: '1.6'
    },
    resultAnswerShell: {
      marginBottom: isSmallPhone ? '12px' : (isMobile ? '16px' : '24px')
    },
    cardFooter: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginTop: isSmallPhone ? '12px' : (isMobile ? '16px' : '24px'),
      paddingTop: isSmallPhone ? '12px' : (isMobile ? '16px' : '20px'),
      borderTop: '1px solid rgba(97, 218, 251, 0.1)',
      flexDirection: isMobile ? 'column' : 'row',
      gap: isSmallPhone ? '8px' : (isMobile ? '12px' : '0'),
      textAlign: isMobile ? 'center' : 'left'
    },
    scriptureInfo: {
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      color: '#a0aec0',
      fontSize: isSmallPhone ? '0.8rem' : (isMobile ? '0.85rem' : '0.9rem')
    },
    wisdomNote: {
      color: '#61dafb',
      fontSize: isSmallPhone ? '0.75rem' : (isMobile ? '0.8rem' : '0.85rem'),
      fontWeight: '600',
      fontStyle: 'italic'
    },
    loadingExperience: {
      background: 'linear-gradient(135deg, rgba(10, 16, 26, 0.96) 0%, rgba(19, 30, 48, 0.94) 52%, rgba(12, 19, 28, 0.96) 100%)',
      borderRadius: isSmallPhone ? '16px' : (isMobile ? '18px' : '24px'),
      padding: isSmallPhone ? '18px' : (isMobile ? '22px' : '30px'),
      border: '1px solid rgba(97, 218, 251, 0.18)',
      boxShadow: '0 24px 70px rgba(0, 0, 0, 0.35), 0 0 35px rgba(97, 218, 251, 0.08)',
      overflow: 'hidden',
      position: 'relative'
    },
    loadingEyebrow: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '10px',
      padding: '8px 14px',
      borderRadius: '999px',
      background: 'rgba(97, 218, 251, 0.1)',
      border: '1px solid rgba(97, 218, 251, 0.16)',
      color: '#8be7ff',
      fontSize: isSmallPhone ? '0.72rem' : '0.8rem',
      fontWeight: '700',
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
      marginBottom: isSmallPhone ? '14px' : '18px'
    },
    loadingTitle: {
      color: '#ffffff',
      fontSize: isSmallPhone ? '1.15rem' : (isMobile ? '1.35rem' : '1.8rem'),
      fontWeight: '800',
      lineHeight: '1.25',
      marginBottom: '10px'
    },
    loadingSubtitle: {
      color: '#a9bfd5',
      fontSize: isSmallPhone ? '0.9rem' : (isMobile ? '0.98rem' : '1.05rem'),
      lineHeight: '1.7',
      maxWidth: '760px',
      marginBottom: isSmallPhone ? '18px' : '24px'
    },
    loadingLayout: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : 'minmax(0, 1.4fr) minmax(250px, 0.9fr)',
      gap: isSmallPhone ? '16px' : (isMobile ? '18px' : '24px'),
      alignItems: 'stretch'
    },
    loadingScanPanel: {
      position: 'relative',
      borderRadius: isSmallPhone ? '14px' : '18px',
      background: 'linear-gradient(180deg, rgba(9, 13, 20, 0.92) 0%, rgba(8, 12, 18, 0.98) 100%)',
      border: '1px solid rgba(97, 218, 251, 0.12)',
      padding: isSmallPhone ? '16px' : (isMobile ? '18px' : '22px'),
      minHeight: isSmallPhone ? '220px' : '260px',
      overflow: 'hidden'
    },
    loadingScanTop: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: '12px',
      marginBottom: isSmallPhone ? '14px' : '18px',
      flexWrap: 'wrap'
    },
    loadingScanHeading: {
      color: '#d8f8ff',
      fontSize: isSmallPhone ? '0.9rem' : '0.98rem',
      fontWeight: '700',
      letterSpacing: '0.03em'
    },
    loadingSignal: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '8px',
      color: '#7dd3fc',
      fontSize: isSmallPhone ? '0.72rem' : '0.78rem',
      fontWeight: '700',
      padding: '6px 10px',
      borderRadius: '999px',
      background: 'rgba(97, 218, 251, 0.08)',
      border: '1px solid rgba(97, 218, 251, 0.14)'
    },
    loadingSignalDot: {
      width: '8px',
      height: '8px',
      borderRadius: '50%',
      background: '#61dafb',
      boxShadow: '0 0 14px rgba(97, 218, 251, 0.7)'
    },
    loadingScriptArea: {
      position: 'relative',
      borderRadius: '16px',
      padding: isSmallPhone ? '16px 14px' : '18px 18px',
      background: 'linear-gradient(180deg, rgba(14, 22, 34, 0.88) 0%, rgba(10, 16, 26, 0.55) 100%)',
      border: '1px solid rgba(97, 218, 251, 0.08)',
      minHeight: isSmallPhone ? '150px' : '178px',
      overflow: 'hidden'
    },
    loadingScriptLine: {
      height: '14px',
      borderRadius: '999px',
      marginBottom: '14px',
      background: 'linear-gradient(90deg, rgba(87, 185, 220, 0.1) 0%, rgba(120, 214, 255, 0.34) 50%, rgba(87, 185, 220, 0.08) 100%)',
      opacity: 0.85
    },
    loadingScriptureNote: {
      marginTop: isSmallPhone ? '12px' : '16px',
      color: '#89a6bf',
      fontSize: isSmallPhone ? '0.78rem' : '0.84rem',
      lineHeight: '1.6'
    },
    loadingStepsCard: {
      borderRadius: isSmallPhone ? '14px' : '18px',
      background: 'linear-gradient(180deg, rgba(13, 19, 31, 0.9) 0%, rgba(9, 14, 22, 0.96) 100%)',
      border: '1px solid rgba(97, 218, 251, 0.12)',
      padding: isSmallPhone ? '16px' : (isMobile ? '18px' : '22px'),
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      gap: isSmallPhone ? '14px' : '18px'
    },
    loadingStepsHeader: {
      color: '#d8f8ff',
      fontSize: isSmallPhone ? '0.92rem' : '1rem',
      fontWeight: '700'
    },
    loadingStepsList: {
      display: 'flex',
      flexDirection: 'column',
      gap: '12px'
    },
    loadingStepItem: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: '12px',
      padding: '10px 0'
    },
    loadingStepMarker: {
      width: '12px',
      height: '12px',
      marginTop: '5px',
      borderRadius: '50%',
      background: 'linear-gradient(135deg, #61dafb 0%, #21a1f1 100%)',
      boxShadow: '0 0 18px rgba(97, 218, 251, 0.5)',
      flexShrink: 0
    },
    loadingStepTitle: {
      color: '#f8fbff',
      fontSize: isSmallPhone ? '0.86rem' : '0.92rem',
      fontWeight: '700',
      marginBottom: '4px'
    },
    loadingStepText: {
      color: '#8fa7bf',
      fontSize: isSmallPhone ? '0.78rem' : '0.84rem',
      lineHeight: '1.55'
    },
    loadingSourcesRow: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '10px',
      marginTop: isSmallPhone ? '18px' : '22px'
    },
    loadingSourceChip: {
      display: 'inline-flex',
      alignItems: 'center',
      padding: isSmallPhone ? '8px 12px' : '9px 14px',
      borderRadius: '999px',
      border: '1px solid rgba(97, 218, 251, 0.15)',
      background: 'rgba(97, 218, 251, 0.08)',
      color: '#9ee8ff',
      fontSize: isSmallPhone ? '0.74rem' : '0.8rem',
      fontWeight: '700'
    },
    loadingFootnote: {
      marginTop: isSmallPhone ? '14px' : '16px',
      color: '#90a5bc',
      fontSize: isSmallPhone ? '0.76rem' : '0.82rem',
      lineHeight: '1.6'
    },

    // Enhanced Questions Section
    questionsSection: {
      marginTop: isSmallPhone ? '24px' : (isMobile ? '32px' : '60px')
    },
    sectionHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: isSmallPhone ? '20px' : (isMobile ? '24px' : '40px'),
      flexDirection: isMobile ? 'column' : 'row',
      gap: isSmallPhone ? '12px' : (isMobile ? '16px' : '0'),
      textAlign: isMobile ? 'center' : 'left'
    },
    sectionTitleMain: {
      fontSize: isSmallPhone ? '1.4rem' : (isMobile ? '1.6rem' : '2.2rem'),
      fontWeight: '800',
      background: 'linear-gradient(135deg, #61dafb 0%, #21a1f1 100%)',
      backgroundClip: 'text',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent'
    },
    viewAllButton: {
      background: 'rgba(97, 218, 251, 0.1)',
      border: '1px solid rgba(97, 218, 251, 0.2)',
      color: '#61dafb',
      padding: isSmallPhone ? '8px 16px' : (isMobile ? '10px 20px' : '12px 24px'),
      borderRadius: '12px',
      fontSize: isSmallPhone ? '0.8rem' : (isMobile ? '0.85rem' : '0.9rem'),
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      border: 'none'
    },
    questionsGrid: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(350px, 1fr))',
      gap: isSmallPhone ? '12px' : (isMobile ? '16px' : '28px')
    },
    questionCard: {
      background: 'linear-gradient(135deg, rgba(15, 15, 15, 0.85) 0%, rgba(20, 20, 20, 0.90) 100%)',
      backdropFilter: 'blur(20px)',
      borderRadius: isSmallPhone ? '16px' : (isMobile ? '18px' : '24px'),
      boxShadow: '0 20px 60px rgba(97, 218, 251, 0.1)',
      padding: isSmallPhone ? '20px' : (isMobile ? '24px' : '36px'),
      border: '1px solid rgba(97, 218, 251, 0.2)',
      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
      position: 'relative',
      overflow: 'hidden'
    },
    questionCardHover: {
      transform: isMobile ? 'none' : 'translateY(-10px) scale(1.02)',
      boxShadow: isMobile ? '0 20px 60px rgba(97, 218, 251, 0.1)' : '0 30px 80px rgba(97, 218, 251, 0.25)',
      borderColor: 'rgba(97, 218, 251, 0.4)'
    },
    cardBackground: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'linear-gradient(135deg, rgba(97, 218, 251, 0.05) 0%, rgba(33, 161, 241, 0.05) 100%)',
      zIndex: 0
    },
    cardContent: {
      position: 'relative',
      zIndex: 1
    },
    cardHeader: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: isSmallPhone ? '12px' : (isMobile ? '16px' : '20px'),
      flexDirection: isMobile ? 'column' : 'row',
      gap: isSmallPhone ? '8px' : (isMobile ? '12px' : '0'),
      textAlign: isMobile ? 'center' : 'left'
    },
    categoryTag: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '6px',
      background: 'linear-gradient(135deg, #61dafb 0%, #21a1f1 100%)',
      color: 'black',
      padding: isSmallPhone ? '6px 12px' : (isMobile ? '8px 14px' : '10px 18px'),
      borderRadius: '14px',
      fontSize: isSmallPhone ? '0.75rem' : (isMobile ? '0.8rem' : '0.85rem'),
      fontWeight: '800',
      boxShadow: '0 6px 20px rgba(97, 218, 251, 0.3)'
    },
    questionIcon: {
      fontSize: isSmallPhone ? '1.3rem' : (isMobile ? '1.5rem' : '2rem'),
      opacity: 0.8
    },
    questionTitle: {
      fontSize: isSmallPhone ? '1.1rem' : (isMobile ? '1.2rem' : '1.4rem'),
      fontWeight: '700',
      color: '#ffffff',
      marginBottom: isSmallPhone ? '10px' : (isMobile ? '12px' : '16px'),
      lineHeight: '1.3',
      textAlign: isMobile ? 'center' : 'left',
      wordBreak: 'break-word'
    },
    questionSummary: {
      color: '#a0aec0',
      fontSize: isSmallPhone ? '0.9rem' : (isMobile ? '0.95rem' : '1.05rem'),
      marginBottom: isSmallPhone ? '16px' : (isMobile ? '20px' : '24px'),
      lineHeight: '1.6',
      textAlign: isMobile ? 'center' : 'left'
    },
    tagsContainer: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '4px',
      marginBottom: isSmallPhone ? '16px' : (isMobile ? '20px' : '24px'),
      justifyContent: isMobile ? 'center' : 'flex-start'
    },
    tag: {
      background: 'rgba(97, 218, 251, 0.1)',
      color: '#61dafb',
      padding: isSmallPhone ? '3px 6px' : (isMobile ? '4px 8px' : '6px 12px'),
      borderRadius: '8px',
      fontSize: isSmallPhone ? '0.65rem' : (isMobile ? '0.7rem' : '0.8rem'),
      fontWeight: '600',
      border: '1px solid rgba(97, 218, 251, 0.2)'
    },
    questionFooter: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      flexDirection: isMobile ? 'column' : 'row',
      gap: isSmallPhone ? '8px' : (isMobile ? '12px' : '0'),
      textAlign: isMobile ? 'center' : 'left'
    },
    date: {
      fontSize: isSmallPhone ? '0.75rem' : (isMobile ? '0.8rem' : '0.9rem'),
      color: '#718096',
      fontWeight: '500'
    },
    answerButton: {
      background: 'linear-gradient(135deg, #61dafb 0%, #21a1f1 100%)',
      color: 'black',
      padding: isSmallPhone ? '10px 20px' : (isMobile ? '12px 24px' : '14px 32px'),
      borderRadius: '14px',
      border: 'none',
      fontSize: isSmallPhone ? '0.85rem' : (isMobile ? '0.9rem' : '1rem'),
      fontWeight: '700',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      boxShadow: '0 6px 25px rgba(97, 218, 251, 0.3)',
      display: 'flex',
      alignItems: 'center',
      gap: '6px'
    }
  };

  // Render main content based on current page
  const renderMainContent = () => {
    switch (currentPage) {
      case 'about':
        return renderClosablePage(<AboutSection />, true);
      case 'terms':
        return renderClosablePage(<TermsConditionsSection />, true);
      case 'privacy':
        return renderClosablePage(<PrivacyPolicy />, true);
      case 'forum':
        return renderClosablePage(<PublicForum />);
      case 'feedback':
        return renderClosablePage(<Feedback />);
      case 'contact':
        return renderClosablePage(<ContactUs />);
      case 'home':
      default:
        return (
          <>
            <section style={styles.hero}>
              <div style={styles.heroBackground}></div>
              <div style={styles.heroContent}>
                <h1 style={styles.heroTitle}>Ask. Learn. Evolve</h1>
                <p style={styles.heroSubtitle}>
                  Practical guidance from Gita, Upanishads, Itihasas and more, explained in simple language for contemporary challenges.
                </p>
                


                {/* <div style={styles.heroSearch}>
                  <input
                    type="text"
                    placeholder="e.g. Is anger always wrong? How to find peace?"
                    style={styles.heroInput}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleHeroSearch()}
                  />
                  <button
                    style={{
                      ...styles.heroSearchButton,
                      ...(hoverStates.heroSearch ? { transform: 'translateY(-2px) scale(1.05)' } : {}),
                    }}
                    onMouseEnter={() => handleMouseEnter('heroSearch')}
                    onMouseLeave={() => handleMouseLeave('heroSearch')}
                    onClick={handleHeroSearch}
                    disabled={isLoading}
                  >
                    {isLoading ? 'Searching...' : 'Get Scripture Answer'}
                  </button>
                </div> */}






                <div style={styles.heroSearch}>
  <div style={{ position: 'relative', width: '100%' }}>
    <input
      type="text"
      placeholder={isListening ? "Listening..." : "e.g. Is anger always wrong? How to find peace?"}
      style={{
        ...styles.heroInput,
        paddingRight: isSupported ? (isSmallPhone ? '52px' : '56px') : (isSmallPhone ? '16px' : '30px'),
      }}
      value={searchTerm}
      onChange={(e) => setSearchTerm(e.target.value)}
      onKeyPress={(e) => e.key === 'Enter' && handleHeroSearch()}
    />
    {isSupported && (
      <button
        type="button"
        onClick={toggleListening}
        style={{
          position: 'absolute',
          right: isSmallPhone ? '12px' : '16px',
          top: '50%',
          transform: 'translateY(-50%)',
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          color: isListening ? '#ef4444' : 'rgba(97,218,251,0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '4px',
          width: '32px',
          height: '32px',
          borderRadius: '50%',
        }}
        title={isListening ? "Stop" : "Speak your question"}
      >
        {isListening ? (
          <span style={{ fontSize: '14px' }}>🔴</span>
        ) : (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/>
            <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
            <line x1="12" y1="19" x2="12" y2="23"/>
            <line x1="8" y1="23" x2="16" y2="23"/>
          </svg>
        )}
      </button>
    )}
  </div>
  <button
    style={{
      ...styles.heroSearchButton,
      ...(hoverStates.heroSearch ? { transform: 'translateY(-2px) scale(1.05)' } : {}),
    }}
    onMouseEnter={() => handleMouseEnter('heroSearch')}
    onMouseLeave={() => handleMouseLeave('heroSearch')}
    onClick={handleHeroSearch}
    disabled={isLoading}
  >
    {isLoading ? 'Searching...' : 'Get Scripture Answer'}
  </button>
</div>


                <div style={styles.scriptureGrid}>
                  {scriptures.map((scripture, index) => (
                    <div key={index} style={styles.scriptureItem}>
                      <span>{scripture.icon}</span>
                      <span>{scripture.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {showResults && (
              <section ref={resultsSectionRef} style={styles.resultsSlide}>
                <div style={styles.resultsHeader}>
                  <div style={styles.resultsIcon} aria-hidden="true"></div>
                  <h2 style={styles.resultsTitle}>
                    Scripture Guidance for:
                    <span style={styles.searchQuery}> "{displayedResultsQuestion}"</span>
                  </h2>
                </div>
                
                {isLoading ? (
                  <div style={styles.loadingExperience}>
                    <div style={styles.loadingEyebrow}>
                      <span>Live Scripture Search</span>
                    </div>
                    <p style={styles.loadingSubtitle}>
                      Searching across core scriptures and aligning the most relevant verses into one
                      clear answer.
                    </p>

                    <div style={styles.loadingScanPanel}>
                      <div style={styles.loadingScanTop}>
                        <div style={styles.loadingScanHeading}>Searching scripture library</div>
                        <div style={styles.loadingSignal}>
                          <span
                            style={{
                              ...styles.loadingSignalDot,
                              animation: 'scriptureMarkerGlow 1.8s ease-in-out infinite'
                            }}
                          />
                          <span>Scanning texts</span>
                        </div>
                      </div>

                      <div style={styles.loadingScriptArea}>
                        {scriptureScanLines.map((line, index) => (
                          <div
                            key={index}
                            style={{
                              ...styles.loadingScriptLine,
                              width: line.width,
                              animation: `scriptureLinePulse 2.8s ease-in-out ${line.delay} infinite`
                            }}
                          />
                        ))}

                        <div
                          style={{
                            position: 'absolute',
                            inset: '-20% 0 auto 0',
                            height: '44px',
                            background: 'linear-gradient(180deg, rgba(97, 218, 251, 0) 0%, rgba(97, 218, 251, 0.22) 48%, rgba(97, 218, 251, 0) 100%)',
                            filter: 'blur(10px)',
                            animation: 'scriptureScanSweep 3.6s linear infinite'
                          }}
                        />
                      </div>
                    </div>

                    <div style={styles.loadingSourcesRow}>
                      {loadingSources.map((source, index) => (
                        <div
                          key={source}
                          style={{
                            ...styles.loadingSourceChip,
                            animation: `scriptureLinePulse 3.2s ease-in-out ${index * 0.2}s infinite`
                          }}
                        >
                          <span style={{ marginRight: '8px' }}>📘</span>
                          {source}
                        </div>
                      ))}
                    </div>

                    <p style={styles.loadingFootnote}>
                      Gathering wisdom from multiple books before presenting your answer.
                    </p>
                  </div>
                ) : searchResults.length > 0 ? (
                  searchResults.map((result, index) => (
                    <div 
                      key={index}
                      style={styles.resultAnswerShell}
                    >
                      {false && (result.reference || result.scripture) && (
                        <div style={styles.scriptureBadge}>
                          <span>📖</span>
                          <span>{result.reference ? `${result.reference}` : result.scripture}</span>
                        </div>
                      )}
                      
                      <ScriptureAnswer 
                        content={result} 
                        question={result.question || searchTerm}
                      />
                      
                      {false && false && <div style={styles.cardFooter}>
                        <div style={styles.scriptureInfo}>
                          <span>📚</span>
                          <span>Source: {result.scripture || 'Hindu Scriptures'}</span>
                        </div>
                        <div style={styles.wisdomNote}>
                          Ancient wisdom for modern life
                        </div>
                      </div>}
                    </div>
                  ))
                ) : (
                  <div style={{ textAlign: 'center', padding: '30px', color: '#a0aec0' }}>
                    <div style={{ fontSize: '2rem', marginBottom: '12px' }}>🔍</div>
                    <h3>No specific answers found</h3>
                    <p>Try asking about topics like anger management, finding peace, or understanding karma</p>
                  </div>
                )}
              </section>
            )}

            <section style={styles.questionsSection}>
              <div style={styles.sectionHeader}>
                <h2 style={styles.sectionTitleMain}>Featured Questions</h2>
                <button 
                  style={{
                    ...styles.viewAllButton,
                    ...(hoverStates.viewAllQuestions ? { 
                      background: 'rgba(97, 218, 251, 0.2)',
                      borderColor: 'rgba(97, 218, 251, 0.4)',
                      transform: 'translateY(-2px)'
                    } : {})
                  }}
                  onMouseEnter={() => handleMouseEnter('viewAllQuestions')}
                  onMouseLeave={() => handleMouseLeave('viewAllQuestions')}
                  onClick={() => openQuestionLibrary('all')}
                >
                  View All Questions →
                </button>
              </div>
              <div style={styles.questionsGrid}>
                {featuredQuestions.map(question => (
                  <div 
                    key={question.id}
                    style={{
                      ...styles.questionCard,
                      ...(hoverStates[question.id] ? styles.questionCardHover : {})
                    }}
                    onMouseEnter={() => handleMouseEnter(question.id)}
                    onMouseLeave={() => handleMouseLeave(question.id)}
                  >
                    <div style={styles.cardBackground}></div>
                    <div style={styles.cardContent}>
                      <div style={styles.cardHeader}>
                        <span style={styles.categoryTag}>
                          {question.icon} {question.category}
                        </span>
                        <div style={styles.questionIcon}>{question.icon}</div>
                      </div>
                      <h3 style={styles.questionTitle}>{question.title}</h3>
                      <p style={styles.questionSummary}>{question.summary}</p>
                      
                      <div style={styles.tagsContainer}>
                        {question.tags.map((tag, index) => (
                          <span key={index} style={styles.tag}>{tag}</span>
                        ))}
                      </div>
                      
                      <div style={styles.questionFooter}>
                        <span style={styles.date}>{question.date}</span>
                        <button 
                          style={{
                            ...styles.answerButton,
                            ...(hoverStates[`btn-${question.id}`] ? { transform: 'translateY(-2px) scale(1.05)' } : {})
                          }}
                          onMouseEnter={() => handleMouseEnter(`btn-${question.id}`)}
                          onMouseLeave={() => handleMouseLeave(`btn-${question.id}`)}
                          onClick={() => handleSearch(question.title)}
                        >
                          View Answer →
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          </>
        );
    }
  };

  const isAdmin = user?.role === "ADMIN";

  // Render recent questions section with scrollable container
  const renderRecentQuestions = () => (
    <div style={styles.navSection}>
      <h3 style={styles.sectionTitle}>🔥 Recent Questions</h3>
      <div 
        className={isMobile ? 'recent-questions-scroll' : undefined}
        ref={recentQuestionsRef}
        style={{ 
          maxHeight: isMobile ? '300px' : 'none',
          overflowY: isMobile ? 'auto' : 'visible',
          paddingRight: isMobile ? '4px' : '0'
        }}
      >
        {recentQuestions.map((question, index) => (
          <button
            key={index}
            type="button"
            className="sidebar-interactive"
            style={{
              ...styles.recentQuestion,
              ...(hoverStates[`recent-${index}`] ? { 
                transform: 'translateX(8px)',
                borderColor: 'rgba(97, 218, 251, 0.3)',
                background: 'rgba(97, 218, 251, 0.05)'
              } : {})
            }}
              onMouseEnter={() => handleMouseEnter(`recent-${index}`)}
              onMouseLeave={() => handleMouseLeave(`recent-${index}`)}
              onClick={() => {
                handleSearch(question.title);
                setShowSidebar(false);
            }}
          >
            <div style={styles.questionTitleSmall}>{question.title}</div>
            <div style={styles.questionMeta}>
              <div style={styles.questionCategory}>{question.category}</div>
              <div style={styles.questionViews}>👁️ {question.views}</div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );

  // Render quick links section (expandable) - FIXED at bottom
  const renderQuickLinks = () => (
    <div style={styles.quickLinksFixed}>
      <button
        type="button"
        style={{
          ...styles.sectionTitle,
          cursor: 'pointer',
          border: 'none',
          background: 'transparent',
          padding: 0,
          marginBottom: quickLinksExpanded ? '12px' : '20px',
          width: '100%',
          textAlign: 'left'
        }}
        onClick={toggleQuickLinks}
      >
        <span>🔗 Quick Links</span>
        <span style={styles.quickLinksToggleIcon}>
          {quickLinksExpanded ? '▼' : '▶'}
        </span>
      </button>
      
      {quickLinksExpanded && additionalLinks.map((link, index) => (
        <button
          key={index}
          type="button"
          className="sidebar-interactive"
          style={{
            ...styles.additionalLink,
            ...(currentPage === link.page ? styles.additionalLinkActive : {}),
            ...(hoverStates[`link-${index}`] ? { 
              transform: 'translateX(6px)',
              background: 'rgba(97, 218, 251, 0.1)',
              borderColor: 'rgba(97, 218, 251, 0.3)'
            } : {})
          }}
          onMouseEnter={() => handleMouseEnter(`link-${index}`)}
          onMouseLeave={() => handleMouseLeave(`link-${index}`)}
          onClick={() => {
            if (link.page) {
              handlePageChange(link.page);
            }
            setShowSidebar(false);
          }}
        >
          <span style={styles.sidebarItemLabel}>
            <span style={styles.sidebarItemIcon}>{link.icon}</span>
            <span>{link.name}</span>
          </span>
          <span style={styles.sidebarItemArrow}>↗</span>
        </button>
      ))}
    </div>
  );

  // Mobile Sidebar Component
  const MobileSidebar = () => (
    <div style={{
      ...styles.mobileSidebar,
      display: isMobile && showSidebar ? 'flex' : 'none',
      flexDirection: 'column'
    }}>
      {/* Scrollable Content */}
      <div style={{ flex: 1, overflowY: 'auto', marginBottom: '20px' }}>
        {user ? (
          <div style={styles.navSection}>
            <div style={styles.userInfoBox}>
              <div style={styles.userInfoIcon}>👋</div>
              <h3 style={styles.userInfoName}>Namaste, {user.name || user.username}</h3>
              <p style={styles.userInfoEmail}>{user.email}</p>
              
              {isAdmin && (
                <button 
                  className="sidebar-interactive"
                  style={{
                    ...styles.adminPanelButton,
                    ...(hoverStates.mobileAdminButton ? { 
                      backgroundColor: '#4338ca',
                      transform: 'translateY(-1px)'
                    } : {})
                  }}
                  onMouseEnter={() => handleMouseEnter('mobileAdminButton')}
                  onMouseLeave={() => handleMouseLeave('mobileAdminButton')}
                  onClick={() => {
                    navigate('/admin');
                    setShowSidebar(false);
                  }}
                >
                  Admin Panel
                </button>
              )}
              
              <button 
                className="sidebar-interactive"
                style={{
                  ...styles.userInfoButton,
                  ...(hoverStates.mobileLogout ? { 
                    background: 'rgba(239, 68, 68, 0.2)',
                    transform: 'translateY(-1px)'
                  } : {})
                }}
                onMouseEnter={() => handleMouseEnter('mobileLogout')}
                onMouseLeave={() => handleMouseLeave('mobileLogout')}
                onClick={() => {
                  handleLogout();
                  setShowSidebar(false);
                }}
              >
                Logout
              </button>
            </div>
          </div>
        ) : null}

        {/* Categories */}
        <nav style={styles.navSection}>
          <h3 style={styles.sectionTitle}>📚 Categories</h3>
          {sidebarCategories.map((category, index) => (
            <button
              key={index}
              type="button"
              className="sidebar-interactive"
              style={{
                ...styles.categoryItem,
                ...(hoverStates[`cat-${index}`] ? { 
                  transform: 'translateX(8px)',
                  background: 'rgba(97, 218, 251, 0.15)',
                  borderColor: 'rgba(97, 218, 251, 0.3)'
                } : {})
              }}
              onMouseEnter={() => handleMouseEnter(`cat-${index}`)}
              onMouseLeave={() => handleMouseLeave(`cat-${index}`)}
              onClick={() => openQuestionLibrary(category.name)}
            >
              <span>{category.icon} {category.name}</span>
              <span style={styles.categoryCount}>{category.count}</span>
            </button>
          ))}
        </nav>

        {/* Recent Questions - Scrollable */}
        {renderRecentQuestions()}
      </div>

      {/* Fixed Quick Links at Bottom */}
      <div style={{
        flexShrink: 0,
        paddingTop: '20px',
        borderTop: '1px solid rgba(97, 218, 251, 0.1)'
      }}>
        {renderQuickLinks()}
      </div>
    </div>
  );

  const loadingQuery = searchTerm.trim() || "your question";
  const displayedResultsQuestion = isLoading
    ? loadingQuery
    : (searchResults[0]?.question || loadingQuery);
  const loadingScanAnimations = `
    @keyframes scriptureScanSweep {
      0% { transform: translateY(-110%); opacity: 0; }
      18% { opacity: 0.95; }
      50% { opacity: 0.75; }
      100% { transform: translateY(310%); opacity: 0; }
    }

    @keyframes scriptureLinePulse {
      0%, 100% { opacity: 0.42; transform: scaleX(0.985); }
      50% { opacity: 1; transform: scaleX(1); }
    }

    @keyframes scriptureMarkerGlow {
      0%, 100% { opacity: 0.45; box-shadow: 0 0 0 rgba(97, 218, 251, 0); }
      50% { opacity: 1; box-shadow: 0 0 18px rgba(97, 218, 251, 0.45); }
    }
  `;
  const scriptureScanLines = [
    { width: '92%', delay: '0s' },
    { width: '84%', delay: '0.25s' },
    { width: '88%', delay: '0.5s' },
    { width: '76%', delay: '0.75s' },
    { width: '90%', delay: '1s' },
    { width: '68%', delay: '1.25s' }
  ];
  const loadingSteps = [
    {
      title: 'Reading core verses',
      text: 'Matching your question with foundational references from major scriptures.'
    },
    {
      title: 'Comparing meanings',
      text: 'Cross-checking context, commentary, and practical interpretation.'
    },
    {
      title: 'Preparing guidance',
      text: 'Shaping the answer into clear language for modern life.'
    }
  ];
  const loadingSources = ['Bhagavad Gita', 'Upanishads', 'Ramayana', 'Mahabharata'];

  return (
    <div style={styles.app}>
      <style>{loadingScanAnimations}</style>
      <div style={styles.backgroundGraphic}></div>

      <div style={styles.mobileSidebarOverlay} onClick={() => setShowSidebar(false)}></div>

      {/* Top Bar with Home Button */}
      <header style={styles.topBar}>
        <div style={styles.topBarContent}>
          <div style={styles.logo} onClick={handleLogoClick}>
            <div style={styles.logoIcon}>🕉️</div>
            <span style={styles.logoText}>DharmaAnswers</span>
          </div>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: isSmallPhone ? '6px' : '10px' }}>
            {/* Home Button - Always visible */}
            <button 
              style={{
                ...styles.homeButton,
                ...(hoverStates.homeButton ? { 
                  background: 'rgba(97, 218, 251, 0.2)',
                  borderColor: 'rgba(97, 218, 251, 0.4)',
                  transform: 'translateY(-1px)'
                } : {})
              }}
              onMouseEnter={() => handleMouseEnter('homeButton')}
              onMouseLeave={() => handleMouseLeave('homeButton')}
              onClick={handleGoHome}
            >
              🏠
            </button>

            {user ? (
              // When logged in, only show mobile menu button on mobile
              isMobile && (
                <button style={styles.mobileMenuButton} onClick={toggleSidebar}>
                  ☰
                </button>
              )
            ) : (
              // When not logged in, show login button and mobile menu
              <>
                <button 
                  style={{
                    ...styles.loginButtonTopBar,
                    ...(hoverStates.topLogin ? { 
                      background: 'rgba(97, 218, 251, 0.2)',
                      borderColor: 'rgba(97, 218, 251, 0.4)',
                      transform: 'translateY(-1px)'
                    } : {})
                  }}
                  onMouseEnter={() => handleMouseEnter('topLogin')}
                  onMouseLeave={() => handleMouseLeave('topLogin')}
                  onClick={handleLoginClick}
                >
                  Login
                </button>

                {isMobile && (
                  <button style={styles.mobileMenuButton} onClick={toggleSidebar}>
                    ☰
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </header>

      {/* Mobile Sidebar */}
      <MobileSidebar />

      {/* Main Content Area */}
      <div style={styles.mainContent}>
        {/* Desktop Sidebar - Fixed Position */}
        <aside style={styles.sidebar} ref={sidebarRef}>
          {/* Scrollable Content */}
          <div className="sidebar-scroll" style={styles.sidebarScrollableContent}>
            {/* FIXED: User info in sidebar only */}
            {user ? (
              <div style={styles.navSection}>
                <div style={styles.userInfoBox}>
                  <div style={styles.userInfoIcon}>👋</div>
                  <h3 style={styles.userInfoName}>Namaste, {user.name || user.username}</h3>
                  <p style={styles.userInfoEmail}>{user.email}</p>
                  
                  {isAdmin && (
                    <button 
                      className="sidebar-interactive"
                      style={{
                        ...styles.adminPanelButton,
                        ...(hoverStates.sidebarAdminButton ? { 
                          backgroundColor: '#4338ca',
                          transform: 'translateY(-1px)'
                        } : {})
                      }}
                      onMouseEnter={() => handleMouseEnter('sidebarAdminButton')}
                      onMouseLeave={() => handleMouseLeave('sidebarAdminButton')}
                      onClick={() => navigate('/admin')}
                    >
                      Admin Panel
                    </button>
                  )}
                  
                  <button 
                    className="sidebar-interactive"
                    style={{
                      ...styles.userInfoButton,
                      ...(hoverStates.desktopLogout ? { 
                        background: 'rgba(239, 68, 68, 0.2)',
                        transform: 'translateY(-1px)'
                      } : {})
                    }}
                    onMouseEnter={() => handleMouseEnter('desktopLogout')}
                    onMouseLeave={() => handleMouseLeave('desktopLogout')}
                    onClick={handleLogout}
                  >
                    Logout
                  </button>
                </div>
              </div>
            ) : null}

            {/* Categories */}
            <nav style={styles.navSection}>
              <h3 style={styles.sectionTitle}>📚 Categories</h3>
              {sidebarCategories.map((category, index) => (
                <button
                  key={index}
                  type="button"
                  className="sidebar-interactive"
                  style={{
                    ...styles.categoryItem,
                    ...((showAllQuestions && selectedQuestionCategory === category.name) ? styles.additionalLinkActive : {}),
                    ...(hoverStates[`cat-${index}`] ? { 
                      transform: 'translateX(6px)',
                      background: 'rgba(97, 218, 251, 0.1)',
                      borderColor: 'rgba(97, 218, 251, 0.3)'
                    } : {})
                  }}
                  onMouseEnter={() => handleMouseEnter(`cat-${index}`)}
                  onMouseLeave={() => handleMouseLeave(`cat-${index}`)}
                  onClick={() => openQuestionLibrary(category.name)}
                >
                  <span style={styles.sidebarItemLabel}>
                    <span style={styles.sidebarItemIcon}>{category.icon}</span>
                    <span>{category.name}</span>
                  </span>
                  <span style={styles.categoryCount}>{category.count}</span>
                </button>
              ))}
            </nav>

            {/* Recent Questions - Scrollable */}
            {renderRecentQuestions()}
          </div>

          {/* Fixed Quick Links at Bottom */}
          {renderQuickLinks()}
        </aside>

        {/* Main Content */}
        <main style={styles.mainArea}>
          {renderMainContent()}
        </main>
      </div>

      {/* Login Modal */}
      {showLogin && (
        <Login 
          onClose={() => setShowLogin(false)}
          onLogin={handleLogin}
        />
      )}

      {/* All Questions Modal */}
      <AllQuestionsModal
        isOpen={showAllQuestions}
        onClose={() => setShowAllQuestions(false)}
        onQuestionClick={handleQuestionClickFromModal}
        initialCategory={selectedQuestionCategory}
        isMobile={isMobile}
        isSmallPhone={isSmallPhone}
      />
    </div>
  );
}

// Main App Component with Router
function App() {
  return (
    <Router>
      <Routes>
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/reset-password" element={<ResetPassword />} />

        {/* Public app (your current UI) */}
        <Route path="/*" element={<MainApp />} />

        {/* Admin area, protected */}
        <Route
          path="/admin/*"
          element={
            <RequireAdmin>
              <AdminLayout />
            </RequireAdmin>
          }
        >
          <Route index element={<Dashboard />} />
          <Route path="users" element={<Users />} />
          <Route path="questions/pending" element={<QuestionsPending />} />
          <Route path="questions/answered" element={<QuestionsAnswered />} />
          <Route path="questions" element={<QuestionsAll />} />
          <Route path="search-history" element={<SearchHistory />} />
          <Route path="activity-logs" element={<ActivityLogs />} />
          <Route path="feedback" element={<AdminFeedback />} />
        </Route>
      </Routes>
    </Router>
  );
}

export default App;
